import type { PropsWithChildren } from "react";
import { Link, Navigate, Outlet } from "react-router-dom";

import { T } from "@repo/transifex";
import { useProfiles } from "~/hooks/queries/profiles";
import { ErrorMessage, ProfileCard, ProfileCardSkeleton } from "~/components";
import { AuthButtons } from "~/components/auth-buttons";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { useAuth } from "~/providers/store/auth";
import {
  getSellRouteForTill,
  isUnattendedTill
} from "~/utils/tills/is-unattended-till";

export const ProfilesPage = () => {
  const { data: profiles, status } = useProfiles();
  const { data: till } = useCurrentTill();
  const auth = useAuth();

  if (auth.status === "unattended" && isUnattendedTill(till)) {
    return <Navigate to={getSellRouteForTill(till)} replace />;
  }

  if (status === "pending")
    return (
      <ProfileLayout>
        <ProfileCardSkeleton />
        <ProfileCardSkeleton />
        <ProfileCardSkeleton />
      </ProfileLayout>
    );

  if (status === "error")
    return (
      <ProfileLayout>
        <ErrorMessage error={<T _str="Could not load profiles" />} />
      </ProfileLayout>
    );

  return (
    <>
      <ProfileLayout>
        {profiles.map(({ name, id }) => {
          return (
            <Link key={id} to={id}>
              <ProfileCard key={id} name={name} />
            </Link>
          );
        })}
      </ProfileLayout>

      <Outlet />
    </>
  );
};

const ProfileLayout = ({ children }: PropsWithChildren) => {
  return (
    <div className="size-full overflow-y-scroll bg-slate-800 p-10">
      <div className="relative mb-11 flex justify-center">
        <h1 className="text-5xl font-bold text-white">
          <T _str="Select user" />
        </h1>

        <AuthButtons className="absolute right-0" />
      </div>

      <div className="flex justify-center">
        <div className="grid grid-cols-2 gap-5 lg:grid-cols-3 xl:grid-cols-4">
          {children}
        </div>
      </div>
    </div>
  );
};
